import _ from 'lodash';
import { getIntl } from 'umi';

const formatEnumKey = (key, casing = 'lower') => {
  return casing === 'camel' ? _.camelCase(key) : String(key).toLowerCase();
};

const resolveEnumTranslationKey = (
  translationPrefix,
  enumKey,
  fallbackKey,
  casingPreference = ['lower', 'camel'],
) => {
  const intl = getIntl?.();
  const messages = intl?.messages || {};
  if (enumKey) {
    for (const casing of casingPreference) {
      const candidate = `${translationPrefix}.${formatEnumKey(enumKey, casing)}`;
      if (!casing || casing === 'lower') {
        if (messages[candidate]) return candidate;
      } else if (messages[candidate]) {
        return candidate;
      }
    }
  }
  return fallbackKey;
};
/**
 * Creates options array for select components from enum objects
 * @param {Object} enumObject - The enum object (e.g., AIProvider, RpaAgentPlatform)
 * @param {string} translationPrefix - The prefix for translation keys (e.g., 'agents.ai.provider')
 * @returns {Array} Array of options with value, label, and locale properties
 */
export const createEnumOptions = (enumObject, translationPrefix, options = {}) => {
  const { casing = 'lower' } = options;
  return Object.keys(enumObject)
    .map((key) => ({
      value: enumObject[key],
      label: `${translationPrefix}.${formatEnumKey(key, casing)}`,
      locale: true,
    }))
    .sort((a, b) => a.value - b.value);
};

/**
 * Gets the translation key for an enum value
 * @param {Object} enumObject - The enum object
 * @param {number} value - The enum value
 * @param {string} translationPrefix - The prefix for translation keys
 * @returns {string} The translation key (always returns a valid key)
 */
export const getEnumTranslationKey = (enumObject, value, translationPrefix) => {
  const actualValue = value === undefined || value === null ? 0 : value;
  const key = Object.keys(enumObject).find((k) => enumObject[k] === actualValue);
  return resolveEnumTranslationKey(translationPrefix, key, `${translationPrefix}.${actualValue}`);
};

/**
 * Specific helper for AI Provider options
 */
export const getAIProviderOptions = (enumObject) => {
  return createEnumOptions(enumObject, 'agents.ai.provider');
};

/**
 * Gets AI Provider translation key
 */
export const getAIProviderTranslationKey = (enumObject, value) => {
  return getEnumTranslationKey(enumObject, value, 'agents.ai.provider');
};

/**
 * Specific helper for RPA Platform options
 */
export const getRPAPlatformOptions = (enumObject) => {
  return createEnumOptions(enumObject, 'agents.rpa.platform');
};

/**
 * Gets RPA Platform translation key
 */
export const getRPAPlatformTranslationKey = (enumObject, value) => {
  return getEnumTranslationKey(enumObject, value, 'agents.rpa.platform');
};

/**
 * Specific helper for RPA Status options
 */
export const getRPAStatusOptions = (enumObject) => {
  return createEnumOptions(enumObject, 'agents.rpa.status');
};

/**
 * Gets RPA Status translation key
 */
export const getRPAStatusTranslationKey = (enumObject, value) => {
  return getEnumTranslationKey(enumObject, value, 'agents.rpa.status');
};

/**
 * Gets the enum name (key) from an enum value
 * If value is null or undefined, defaults to 0
 * @param {Object} enumObject - The enum object (e.g., OperationResultStatus, AutomyAIHandlerType)
 * @param {number|string} value - The enum value to find
 * @returns {string|undefined} The enum name as-is, or undefined if not found
 */
export const getEnumValue = (enumObject, value) => {
  const code = value !== null && value !== undefined ? parseInt(value, 10) : 0;
  const enumKey = Object.keys(enumObject).find((key) => enumObject[key] === code);
  return enumKey;
};

/**
 * Transforms EnumMember values from C# backend to translation keys
 * @param {string} enumMemberValue - The EnumMember value (e.g., "api-key-in-header")
 * @returns {string} The transformed key for translations (e.g., "apikeyinheader")
 */
export const transformEnumMemberToKey = (enumMemberValue) => {
  return enumMemberValue
    .toLowerCase()
    .replace(/-/g, '') // remover guiones
    .replace(/\s+/g, ''); // remover espacios
};
